//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { CompanyTodoCardData, CompanyTodoGroupCount, CompanyTodoLoadState } from "models/propTypes/allCompaniesPropTypes";
//#endregion

const toGroupCounts = (summary: WintSuperkollAPIModel.TodoSummary): CompanyTodoGroupCount[] => {
  // The summary is per group and sub type; the card shows one count per group.
  const counts = new Map<WintSuperkollAPIModel.TodoGroup, number>();
  summary.todosGroupSummary?.forEach((groupSummary) =>
    counts.set(groupSummary.group, (counts.get(groupSummary.group) ?? 0) + groupSummary.todosCount)
  );

  return [...counts.entries()]
    .filter(([, count]) => count > 0)
    .sort(([, first], [, second]) => second - first)
    .map(([group, count]) => ({ group, count }));
};

const toOverdueCount = (summary: WintSuperkollAPIModel.TodoSummary) =>
  summary.todosUrgencySummary
    ?.filter((urgencySummary) => urgencySummary.todoUrgency === WintSuperkollAPIModel.TodoUrgency.Overdue)
    .reduce((sum, urgencySummary) => sum + urgencySummary.todosCount, 0) ?? 0;

export const toCompanyTodoCard = (
  company: WintSuperkollAPIModel.Company,
  state: CompanyTodoLoadState,
  summary?: WintSuperkollAPIModel.TodoSummary
): CompanyTodoCardData => ({
  companyId: company.id,
  companyName: company.name ?? "",
  companyUrl: company.url,
  companyOrg: company.org,
  state: state,
  total: summary?.totalTodosCount ?? 0,
  overdue: summary ? toOverdueCount(summary) : 0,
  groupCounts: summary ? toGroupCounts(summary) : [],
});

/**
 * Last loaded card per person and company, kept for the browser session so reopening the overview
 * shows the previous counts at once. The selected company's entry is also kept current from the
 * dashboard's own summary, see useSyncSelectedCompanyTodoCard.
 */
const cards = new Map<string, CompanyTodoCardData>();
let lastRefreshedAt: Date | undefined;

const getKey = (personId: number, companyId: number) => `${personId}:${companyId}`;

export const getCachedCompanyTodoCard = (personId: number, companyId: number) => cards.get(getKey(personId, companyId));

export const setCachedCompanyTodoCard = (personId: number, card: CompanyTodoCardData) =>
  cards.set(getKey(personId, card.companyId), card);

/** When the overview last finished loading every company, undefined until the first time. */
export const getCompanyTodoCardsRefreshedAt = () => lastRefreshedAt;

export const markCompanyTodoCardsRefreshed = (at: Date) => {
  lastRefreshedAt = at;
};
